import type { TableColumn } from '@shared/table/table';

export type PackListColumn = {
  columnKey: string;
  label: string;
  sourcePath: string;
  sortable?: boolean;
  visible?: boolean;
  width?: number | string;
  align?: string;
};

type ListColumnRole =
  | 'skip'
  | 'name'
  | 'contact'
  | 'location'
  | 'product'
  | 'generatedDate'
  | 'aiScore'
  | 'source'
  | 'priority'
  | 'status'
  | 'text';

function normalizeToken(value: string): string {
  return value.toLowerCase().replace(/[\s_-]/g, '');
}

function columnRole(column: PackListColumn): ListColumnRole {
  const path = normalizeToken(column.sourcePath ?? '');
  const label = normalizeToken(column.label ?? '');

  if (
    path === 'id' ||
    path === 'opportunityid' ||
    label === 'id' ||
    path === 'applicanttype' ||
    label === 'applicanttype'
  ) {
    return 'skip';
  }
  if (
    path === 'name' ||
    path === 'fullname' ||
    path === 'organizationname' ||
    path === 'opportunitynumber' ||
    label === 'name' ||
    label === 'opportunitynumber'
  ) {
    return 'name';
  }
  if (
    path === 'email' ||
    path === 'phone' ||
    path === 'mobile' ||
    path === 'mobilemasked' ||
    path === 'contact' ||
    label === 'contact' ||
    label === 'email'
  ) {
    return 'contact';
  }
  if (path === 'location' || path === 'preferredbranchlabel' || label === 'location') {
    return 'location';
  }
  if (path === 'product' || path === 'productlabel' || path === 'productid' || label === 'product') {
    return 'product';
  }
  if (path === 'generateddate' || path === 'createdat' || label === 'generateddate') {
    return 'generatedDate';
  }
  if (path === 'aiscore' || path === 'opportunityscore' || label === 'aiscore') {
    return 'aiScore';
  }
  if (
    path === 'source' ||
    path === 'sourcetypecode' ||
    path === 'sourcetypelabel' ||
    label === 'source'
  ) {
    return 'source';
  }
  if (path === 'priority' || path === 'prioritycode' || path === 'prioritylabel' || label === 'priority') {
    return 'priority';
  }
  if (path === 'status' || label === 'status') {
    return 'status';
  }
  return 'text';
}

function columnWidth(column: PackListColumn): string | undefined {
  return typeof column.width === 'number' ? `${column.width}px` : column.width;
}

/**
 * Map a pack dataset column onto the existing Opportunity table presentation
 * (two-line name/contact, badges) without changing workflow JSON.
 */
export function presentOpportunityListColumn(
  column: PackListColumn,
): TableColumn<Record<string, unknown>> | null {
  const role = columnRole(column);
  if (role === 'skip') {
    return null;
  }

  const align = (column.align as 'left' | 'center' | 'right') || 'left';
  const base: TableColumn<Record<string, unknown>> = {
    key: role === 'text' ? String(column.sourcePath || column.columnKey) : role,
    label: column.label,
    sortable: column.sortable,
    width: columnWidth(column),
    align,
    type: 'text',
  };

  switch (role) {
    case 'name':
      return {
        ...base,
        key: 'name',
        label: 'NAME',
        lines: [
          {
            key: 'opportunityNumber',
            class: 'text-xs font-medium text-purple-600 dark:text-purple-400 cursor-pointer',
            cursorPointer: true,
          },
          {
            key: 'name',
            class: '2xl:text-sm text-xs font-bold text-gray-900 dark:text-gray-100',
          },
        ],
      };
    case 'contact':
      return {
        ...base,
        key: 'contact',
        label: 'CONTACT',
        lines: [
          { key: 'email', icon: 'mail', class: '2xl:text-sm text-xs' },
          { key: 'phone', icon: 'phone', class: '2xl:text-sm text-xs' },
        ],
      };
    case 'location':
      return { ...base, key: 'location', label: column.label || 'LOCATION' };
    case 'product':
      return { ...base, key: 'product', label: column.label || 'PRODUCT' };
    case 'generatedDate':
      return { ...base, key: 'generatedDate', label: column.label || 'GENERATED DATE' };
    case 'aiScore':
      return {
        ...base,
        key: 'aiScore',
        label: column.label || 'AI SCORE',
        suffix: '%',
        align: 'center',
      };
    case 'source':
      return {
        ...base,
        key: 'source',
        label: column.label || 'SOURCE',
      };
    case 'priority':
      return {
        ...base,
        key: 'priority',
        label: column.label || 'PRIORITY',
        type: 'badge',
        badgeVariant: 'priority',
        align: 'center',
      };
    case 'status':
      return {
        ...base,
        key: 'status',
        label: column.label || 'STATUS',
        type: 'badge',
        badgeVariant: 'status',
        align: 'center',
      };
    default:
      return base;
  }
}
